/**
 * Language and market of a tested site: the visitors browse it as people of that country would (browser
 * locale, Accept-Language, time zone, the local Google) and think and type in its language.
 */

export const SITE_LANGUAGES = ['en', 'it', 'es', 'fr', 'de', 'pt', 'nl'] as const;
export type SiteLanguage = (typeof SITE_LANGUAGES)[number];

export const siteLanguageLabel: Record<SiteLanguage, string> = {
  en: 'English',
  it: 'Italian',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  pt: 'Portuguese',
  nl: 'Dutch',
};

export interface Market {
  /** ISO 3166 country code. */
  code: string;
  country: string;
  language: SiteLanguage;
  timezone: string;
  /** Host of the local Google search, used as the referrer of search visits. */
  google: string;
  phoneExample: string;
  postcodeExample: string;
  cityExample: string;
}

export const MARKETS: Record<string, Market> = {
  US: { code: 'US', country: 'United States', language: 'en', timezone: 'America/New_York', google: 'www.google.com', phoneExample: '+1 212 555 0147', postcodeExample: '10001', cityExample: 'New York' },
  GB: { code: 'GB', country: 'United Kingdom', language: 'en', timezone: 'Europe/London', google: 'www.google.co.uk', phoneExample: '+44 20 7946 0958', postcodeExample: 'SW1A 1AA', cityExample: 'London' },
  IE: { code: 'IE', country: 'Ireland', language: 'en', timezone: 'Europe/Dublin', google: 'www.google.ie', phoneExample: '+353 1 555 0147', postcodeExample: 'D02 X285', cityExample: 'Dublin' },
  IT: { code: 'IT', country: 'Italy', language: 'it', timezone: 'Europe/Rome', google: 'www.google.it', phoneExample: '+39 333 123 4567', postcodeExample: '20121', cityExample: 'Milano' },
  ES: { code: 'ES', country: 'Spain', language: 'es', timezone: 'Europe/Madrid', google: 'www.google.es', phoneExample: '+34 612 345 678', postcodeExample: '28013', cityExample: 'Madrid' },
  FR: { code: 'FR', country: 'France', language: 'fr', timezone: 'Europe/Paris', google: 'www.google.fr', phoneExample: '+33 6 12 34 56 78', postcodeExample: '75001', cityExample: 'Paris' },
  DE: { code: 'DE', country: 'Germany', language: 'de', timezone: 'Europe/Berlin', google: 'www.google.de', phoneExample: '+49 151 23456789', postcodeExample: '10115', cityExample: 'Berlin' },
  AT: { code: 'AT', country: 'Austria', language: 'de', timezone: 'Europe/Vienna', google: 'www.google.at', phoneExample: '+43 660 1234567', postcodeExample: '1010', cityExample: 'Wien' },
  CH: { code: 'CH', country: 'Switzerland', language: 'de', timezone: 'Europe/Zurich', google: 'www.google.ch', phoneExample: '+41 79 123 45 67', postcodeExample: '8001', cityExample: 'Zürich' },
  PT: { code: 'PT', country: 'Portugal', language: 'pt', timezone: 'Europe/Lisbon', google: 'www.google.pt', phoneExample: '+351 912 345 678', postcodeExample: '1100-148', cityExample: 'Lisboa' },
  BR: { code: 'BR', country: 'Brazil', language: 'pt', timezone: 'America/Sao_Paulo', google: 'www.google.com.br', phoneExample: '+55 11 91234 5678', postcodeExample: '01310-100', cityExample: 'São Paulo' },
  NL: { code: 'NL', country: 'Netherlands', language: 'nl', timezone: 'Europe/Amsterdam', google: 'www.google.nl', phoneExample: '+31 6 12345678', postcodeExample: '1012 JS', cityExample: 'Amsterdam' },
  BE: { code: 'BE', country: 'Belgium', language: 'nl', timezone: 'Europe/Brussels', google: 'www.google.be', phoneExample: '+32 470 12 34 56', postcodeExample: '1000', cityExample: 'Bruxelles' },
};

/** The market of a language when the domain does not tell (a .com site in Italian is for Italy). */
const DEFAULT_MARKET: Record<SiteLanguage, string> = { en: 'US', it: 'IT', es: 'ES', fr: 'FR', de: 'DE', pt: 'PT', nl: 'NL' };

const TLD_MARKET: Record<string, string> = {
  it: 'IT',
  uk: 'GB',
  ie: 'IE',
  es: 'ES',
  fr: 'FR',
  de: 'DE',
  at: 'AT',
  ch: 'CH',
  pt: 'PT',
  br: 'BR',
  nl: 'NL',
  be: 'BE',
};

export function isSiteLanguage(value: string | null | undefined): value is SiteLanguage {
  return !!value && (SITE_LANGUAGES as readonly string[]).includes(value);
}

/** First two letters of a language tag (`it-IT` → `it`), when we support it. */
export function siteLanguageOf(tag: string | null | undefined): SiteLanguage | null {
  const lang = tag?.trim().split(/[-_]/)[0]?.toLowerCase();
  return isSiteLanguage(lang) ? lang : null;
}

/**
 * Market of a site: its country domain when the language fits it (example.it in Italian is Italy, a .ch
 * site in German is Switzerland), otherwise the main country of the language.
 */
export function marketFor(siteUrl: string, language: SiteLanguage): Market {
  let tld = '';
  try {
    tld = new URL(siteUrl).hostname.toLowerCase().split('.').pop() ?? '';
  } catch {
    // keep the default
  }
  const byTld = MARKETS[TLD_MARKET[tld] ?? ''];
  if (byTld && (byTld.language === language || (tld === 'ch' && ['fr', 'it'].includes(language)) || (tld === 'be' && language === 'fr'))) {
    return byTld.language === language ? byTld : { ...byTld, language };
  }
  return MARKETS[DEFAULT_MARKET[language]]!;
}

/** The stored market code back to its data; unknown codes fall back to the language's main country. */
export function marketOf(code: string | null | undefined, language: SiteLanguage): Market {
  const m = code ? MARKETS[code.toUpperCase()] : undefined;
  return m ? (m.language === language ? m : { ...m, language }) : MARKETS[DEFAULT_MARKET[language]]!;
}

/** Browser locale of a visitor: their own language in the site's country (`it-IT`, `en-GB`). */
export function visitorLocale(language: string, market: Market): string {
  if (language === market.language) return `${language}-${market.code}`;
  return language === 'en' ? 'en-GB' : `${language}-${language.toUpperCase()}`;
}

/** Accept-Language of a visitor: their language first; a local who browses in the site language adds nothing else. */
export function acceptLanguage(language: string, market: Market): string {
  const locale = visitorLocale(language, market);
  const parts = [locale, `${language};q=0.9`];
  if (language !== 'en') parts.push('en;q=0.5');
  return parts.join(',');
}

/** Search referrers point to the local Google of the market. */
export function localizeReferrer(referrer: string | null, market: Market): string | null {
  if (!referrer) return referrer;
  try {
    const u = new URL(referrer);
    if (u.hostname === 'www.google.com') return `https://${market.google}/`;
  } catch {
    return referrer;
  }
  return referrer;
}

const STOPWORDS: Record<SiteLanguage, string[]> = {
  en: ['the', 'and', 'you', 'your', 'for', 'with', 'our', 'are', 'this', 'that', 'from', 'free', 'more', 'about', 'what', 'how', 'all', 'get', 'can', 'will', 'have', 'contact', 'price', 'pricing', 'sign', 'learn'],
  it: ['il', 'lo', 'la', 'gli', 'le', 'di', 'che', 'per', 'con', 'una', 'del', 'della', 'dei', 'delle', 'sono', 'non', 'più', 'come', 'nostro', 'nostri', 'tuo', 'tua', 'chi', 'siamo', 'scopri', 'prezzi', 'contatti', 'anche', 'nel', 'nella', 'alla', 'questo'],
  es: ['el', 'los', 'las', 'de', 'que', 'para', 'con', 'una', 'del', 'por', 'como', 'más', 'nuestro', 'nuestros', 'tu', 'sus', 'es', 'son', 'precios', 'contacto', 'también', 'este', 'esta', 'sobre'],
  fr: ['le', 'les', 'des', 'de', 'et', 'pour', 'avec', 'une', 'du', 'est', 'sont', 'nous', 'vous', 'votre', 'vos', 'plus', 'comme', 'sur', 'dans', 'tarifs', 'contact', 'aussi', 'cette', 'qui'],
  de: ['der', 'die', 'das', 'und', 'für', 'mit', 'ein', 'eine', 'ist', 'sind', 'wir', 'sie', 'ihr', 'ihre', 'unsere', 'nicht', 'auch', 'auf', 'den', 'dem', 'preise', 'kontakt', 'mehr', 'über', 'jetzt'],
  pt: ['o', 'os', 'as', 'de', 'que', 'para', 'com', 'uma', 'do', 'da', 'dos', 'das', 'por', 'como', 'mais', 'nosso', 'nossos', 'seu', 'sua', 'são', 'preços', 'contato', 'contacto', 'também', 'não'],
  nl: ['de', 'het', 'een', 'en', 'van', 'voor', 'met', 'zijn', 'wij', 'onze', 'jouw', 'uw', 'niet', 'ook', 'op', 'aan', 'bij', 'prijzen', 'meer', 'over', 'hoe', 'wat', 'nu'],
};

/**
 * Language of a text from its most common words. Null when there is too little text to tell. The `lang`
 * of the page is a hint only: many generated sites keep `lang="en"` on Italian content.
 */
export function detectLanguage(text: string): { language: SiteLanguage; confidence: number } | null {
  const words = text
    .toLowerCase()
    .split(/[^a-zà-öø-ÿ]+/i)
    .filter((w) => w.length > 0)
    .slice(0, 4000);
  if (words.length < 15) return null;
  const sets = Object.fromEntries(SITE_LANGUAGES.map((l) => [l, new Set(STOPWORDS[l])])) as Record<SiteLanguage, Set<string>>;
  const scores = SITE_LANGUAGES.map((l) => ({ l, n: words.reduce((s, w) => s + (sets[l].has(w) ? 1 : 0), 0) })).sort((a, b) => b.n - a.n);
  const [best, second] = scores;
  if (!best || best.n < 4) return null;
  const confidence = best.n / (best.n + (second?.n ?? 0));
  return confidence >= 0.55 ? { language: best.l, confidence } : null;
}

/**
 * Language of a site from its pages: the content wins over `<html lang>`, which decides only when the
 * text is too short or mixed.
 */
export function siteLanguageFrom(text: string, htmlLang: string | null): { language: SiteLanguage; from: 'content' | 'html' } | null {
  const detected = detectLanguage(text);
  if (detected) return { language: detected.language, from: 'content' };
  const lang = siteLanguageOf(htmlLang);
  return lang ? { language: lang, from: 'html' } : null;
}
